/**
 * What else is happening while you are away.
 *
 * The portal puts a line under the leave form — "Nobody else in Finance is away
 * those days" — and it is the most useful thing on the sheet: a request is
 * usually approved or refused on whether the team is covered, and the person
 * asking is the only one who cannot see that.
 *
 * Saying it before the request is sent means a clash gets moved rather than
 * refused, which is a better outcome for everyone than a rejection three days
 * later.
 */


const utcDay = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return Date.parse(`${m[1]}-${m[2]}-${m[3]}T00:00:00.000Z`);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** Two ranges overlap if each starts before the other ends. */
export const overlaps = (aFrom, aTo, bFrom, bTo) => {
  const a1 = utcDay(aFrom);
  const a2 = utcDay(aTo) ?? a1;
  const b1 = utcDay(bFrom);
  const b2 = utcDay(bTo) ?? b1;
  if (a1 == null || b1 == null) return false;
  return a1 <= b2 && b1 <= a2;
};

/**
 * Who else is off across the requested dates.
 *
 * Approved leave only: a pending request is not yet an absence, and counting it
 * would tell somebody their week is covered by a colleague whose own request
 * may still be refused. The requester is never their own clash.
 */
export const clashesWith = (entries = [], from, to, selfId = null) => {
  if (!from) return [];
  return (entries || [])
    .filter((e) => {
      const status = String(e?.status || "").toLowerCase();
      if (status && status !== "approved") return false;
      const id = String(e?.employee?._id || e?.employee || "");
      if (selfId && id === String(selfId)) return false;
      return overlaps(from, to, e.startDate || e.from, e.endDate || e.to);
    })
    .map((e) => ({
      _id: e._id,
      name: `${e.employee?.fname || ""} ${e.employee?.lname || ""}`.trim() || "A colleague",
      department: e.employee?.department?.name || e.employee?.department || null,
    }));
};

const list = (names) =>
  names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * The line itself.
 *
 * Returns a tone as well as the words, because "nobody else is away" is
 * reassurance and "two people are away" is not — and the sheet should not
 * colour a clash as though it were good news.
 */
export const clashNote = (entries = [], from, to, selfId = null, scope = null) => {
  if (!from) return null;
  const clashes = clashesWith(entries, from, to, selfId);
  const where = scope ? ` in ${scope}` : "";
  if (!clashes.length) {
    return { tone: "success", text: `Nobody else${where} is away on those days.` };
  }
  const names = [...new Set(clashes.map((c) => c.name))];
  const shown = names.slice(0, 3);
  const extra = names.length - shown.length;
  return {
    tone: "warning",
    text: `${list(shown)}${extra > 0 ? ` and ${extra} more` : ""}${
      names.length === 1 ? " is" : " are"
    } already away on those days${where}. Your manager will see this too.`,
  };
};

/** "Goes to Priya Raman." — the approver, named rather than implied. */
export const approverLine = (manager) => {
  const name = `${manager?.fname || ""} ${manager?.lname || ""}`.trim();
  if (!name) return "This goes to your manager for approval.";
  return `Goes to ${name}. Once approved, you can still call it off before it starts.`;
};
